import { wcisDomain, nwccDomain } from "@js/nwcc-domains.js";
import { getQueryArgs, getStates } from "@js/nwcc-navbar.js";
import { loadMainNwcc, setArgDetailsData } from "@js/nwcc-template.js";
import { loadJsonFile } from "../../forecast-evolution/forecast-evolution-data.js";
import { renderGrowl } from "@js/nwcc-growl.js";

const sitePageBaseURL = `${wcisDomain}/nwcc/site?sitenum`;

/**
 * @returns The arguments that populate the arg-details custom
 *      html element.
 */
function getPageArgs() {
    return [
        {
            argName: "state",
            argDesc:
                "Selects a state to filter sites, use a two-digit state code. Multiple states can be selected by separating them with a hyphen",
            argExample: "NV-WY",
            argDefault: "ALL",
            elemId: "select--state",
        },
    ];
}

/**
 * This function runs once when the DOM finishes it's
 * initial loading.
 */
export async function windowLoad() {
    // Set the arg-details element up
    setArgDetailsData(getPageArgs());

    // Attach event listeners
    addEventListeners();

    // Get the selected URL arguments
    const queryArgs = getQueryArgs();

    // check for a selected state
    const stateArg = queryArgs?.["state"] || "ALL";
    const stateList = stateArg.split("-").map((s) => s.toUpperCase());

    // populate the lists
    await populateLists(stateList);

    loadMainNwcc();
}
window.onload = windowLoad;

/**
 * Adds a click event to all the nwcc-select buttons.
 * the click event triggers the openSitePage function.
 */
export function addEventListeners() {
    // Get list of nwcc-selects
    const nwccSelects = Array.from(document.getElementsByTagName("nwcc-select"));

    // for each select, add listeners to the button
    nwccSelects.forEach((select) => {
        const networkID = select.dataset.id;
        const btn = document.getElementById(networkID + "-btn");
        btn.onclick = () => {
            openSitePage(networkID);
        };
    });
}

/**
 * Populates the nwcc-select elements with lists of
 * stations retrived from the JSON factory.Hides any
 * empty lists. Station lists are filtered based on the
 * provided list of 'states'
 *
 * @param {Array[String]} states
 */
export async function populateLists(states) {
    // Get all the station metadata
    const metadata = await loadJsonFile(`${nwccDomain}/awdb/sitedata/metadata/ALL/metadata.json`);

    // Iterate over nwcc-selects and populate each list
    const nwccSelects = Array.from(document.getElementsByTagName("nwcc-select"));
    let totalCount = 0;
    nwccSelects.forEach((select) => {
        const networkID = select.dataset.id;

        // Get the list of matching stations for the given network and state.
        const stations = metadata
            .filter((meta) => {
                const [id, state, ntwk] = meta.stationTriplet.split(":");
                return ntwk === networkID.toUpperCase() && (states.includes(state) || states.includes("ALL"));
            })
            .sort((a, b) => a.name.localeCompare(b.name));

        // If there are no stations, remove the select
        if (!stations.length) {
            select.closest("div").classList.add("fsa-hide");
            return;
        }

        // increment the total count
        totalCount += stations.length;

        // populate the badge
        document.getElementById(networkID + "-badge").innerHTML = stations.length;

        // Populate the Drop-down
        const dropDown = document.getElementById(networkID + "-dd");
        stations.forEach((station) => {
            const option = document.createElement("option");
            option.value = `${sitePageBaseURL}=${station.stationTriplet.split(":")[0]}`;
            option.text = `${station.name} (${station.stationTriplet})`;
            dropDown.appendChild(option);
        });
    });

    // Check for the case that we're not displaying any stations
    // and let the user know that we don't have stations in
    // the selected state
    if (!totalCount) {
        const stateItems = getStates();
        const longStates = joinWithAnd(
            states.map((state) => {
                return stateItems.filter((s) => s.abbr.toUpperCase() === state)[0].name;
            }),
        );
        const msg = `NRCS does not operate any sites in ${longStates}. Therefore, no links for site pages can be produced.`;
        renderGrowl(`No sites in ${longStates}`, msg, "error", true, 30);
    }
}

function joinWithAnd(array) {
    if (array.length === 1) return array[0];
    if (array.length === 2) return array.join(" and ");
    return array.slice(0, -1).join(", ") + ", and " + array[array.length - 1];
}

/**
 * Opens the selected site page in a new tab.
 * @param {String} networkID
 */
export function openSitePage(networkID) {
    const select = document.getElementById(networkID + "-dd");
    const url = select.value;
    window.open(url, "_blank");
}
